import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { format } from 'date-fns';
import globalUserPage from '../../../../stores/global-user-page';
import { observer } from 'mobx-react-lite';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import {
  ExamSessionStudent,
  ExamStudentWithCourseDetails,
} from '../../../../api/types/students/exams/exam-student-response';

export const UserPageStudentExams: React.FC = observer(() => {
  const exams = useMemo(() => {
    return globalUserPage.studentDetails?.exams ?? [];
  }, [globalUserPage.studentDetails?.exams]);

  const [expandedRows, setExpandedRows] = useState<any>({});

  const formatDateTime = (date: Date | undefined) => {
    if (!date) return '';
    return format(new Date(date), 'dd/MM/yyyy HH:mm');
  };

  const formatTime = (date: Date | undefined) => {
    if (!date) return '';
    return format(new Date(date), 'HH:mm');
  };

  const pointsTemplate = (rowData: ExamStudentWithCourseDetails) => {
    return rowData.completionGrade ? 'משלים למאה' : `${rowData.points} נקודות`;
  };

  const eligibilityTemplate = (rowData: ExamSessionStudent) => {
    switch (rowData.enrollment.examEligibility) {
      case 'eligibility':
        return <Tag severity="success" value="זכאי" />;
      case 'not_eligibility':
        return <Tag severity="danger" value="לא זכאי" />;
      case 'excluded':
        return <Tag severity="warning" value="מנוע" />;
      default:
        return '';
    }
  };

  const enrollmentTemplate = (rowData: ExamSessionStudent) => {
    return rowData.enrollment ? (
      <Tag severity="success" value="רשום" />
    ) : (
      <Tag severity="danger" value="לא רשום" />
    );
  };

  const attendanceTemplate = (rowData: ExamSessionStudent) => {
    switch (rowData.enrollment.attended) {
      case 'attended':
        return <Tag severity="success" value="נוכח" />;
      case 'not_attended':
        return <Tag severity="danger" value="לא נוכח" />;
      case 'exempt':
        return <Tag severity="info" value="פטור" />;
      default:
        return '';
    }
  };

  const gradeTemplate = (grade: number | undefined) => {
    if (!grade) return '-';
    return (
      <span className={`grade-value ${grade >= 60 ? 'passing' : 'failing'}`}>
        {grade}
      </span>
    );
  };

  const sessionTemplate = (rowData: ExamStudentWithCourseDetails) => {
    if (!rowData.sessions?.length) {
      return <div className="p-text-center p-p-3">אין מועדים זמינים</div>;
    }

    return (
      <DataTable
        value={rowData.sessions}
        className="p-datatable-sm sessions-table"
        style={{ backgroundColor: 'white' }}
        emptyMessage="לא נמצאו מועדים"
      >
        <Column
          body={(session) =>
            `${formatTime(session.startTime)} - ${formatTime(session.endTime)}`
          }
          header="שעות"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="buildingName"
          header="מבנה"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="className"
          header="כיתה"
          style={{ textAlign: 'right' }}
        />
        <Column
          body={enrollmentTemplate}
          header="סטטוס הרשמה"
          style={{ textAlign: 'right' }}
        />
        <Column
          body={eligibilityTemplate}
          header="זכאות להרשמה"
          style={{ textAlign: 'right' }}
        />
        <Column
          body={attendanceTemplate}
          header="נוכחות"
          style={{ textAlign: 'right' }}
        />
        <Column
          body={(rowData) => gradeTemplate(rowData.grade)}
          header="ציון"
          style={{ textAlign: 'right' }}
        />
      </DataTable>
    );
  };

  const getHighestGrade = (exam: ExamStudentWithCourseDetails) => {
    if (!exam.sessions || exam.sessions.length === 0) return 'טרם הוזן ציון';

    const validGrades = exam.sessions
      .map((session) => session.enrollment.grade)
      .filter((grade) => grade !== null && grade !== undefined);

    return validGrades.length > 0 ? Math.max(...validGrades) : 'טרם הוזן ציון';
  };

  return (
    <div style={{ width: '100%', padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>מבחנים</h2>
      <div className="exams-grid">
        {exams.map((exam) => (
          <Card key={exam.id} className="exam-card">
            <div className="exam-header">
              <div className="exam-title">
                <h3>{exam.examName}</h3>
                <div className="exam-subtitle">{exam.courseName}</div>
                <div className="exam-program">
                  מועדים: {exam.sessions.length}
                </div>
              </div>
              <div className="exam-actions">
                <div className="exam-info-group">
                  <div className="info-box scoring-box">
                    <div className="info-icon">
                      <i className="pi pi-calculator"></i>
                    </div>
                    <div className="info-content">
                      <div className="info-label">שיטת ניקוד</div>
                      <div className="info-value">
                        <span className="scoring-method">
                          {pointsTemplate(exam)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="info-box grade-box">
                    <div className="info-icon">
                      <i className="pi pi-chart-bar"></i>
                    </div>
                    <div className="info-content">
                      <div className="info-label">ציון סופי</div>
                      <div className="info-value">
                        <span className="grade-display">
                          {getHighestGrade(exam)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="sessions-info">
                  <Button
                    label={`מועדים: ${exam.sessions?.length || 0}`}
                    icon={
                      expandedRows[exam.id]
                        ? 'pi pi-chevron-up'
                        : 'pi pi-chevron-down'
                    }
                    iconPos="left"
                    className="p-button-outlined p-button-secondary sessions-toggle-btn"
                    onClick={() =>
                      setExpandedRows((prev) => ({
                        ...prev,
                        [exam.id]: !prev[exam.id],
                      }))
                    }
                  />
                </div>
              </div>
            </div>

            {expandedRows[exam.id] && (
              <>
                <Divider />
                <div className="exam-content">
                  <div className="sessions-container">
                    {sessionTemplate(exam)}
                  </div>
                </div>
              </>
            )}
          </Card>
        ))}
      </div>

      <style>{`
        .exams-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .exam-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .exam-card .p-card-body {
          padding: 0;
        }

        .exam-card .p-card-content {
          padding: 0;
        }

        .exam-header {
          padding: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .exam-title {
          flex: 1;
        }

        .exam-title h3 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.25rem;
        }

        .exam-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .sessions-toggle-btn {
          font-size: 0.875rem;
          padding: 0.4rem 0.8rem;
          height: fit-content;
          white-space: nowrap;
          min-width: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .sessions-toggle-btn .p-button-label {
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .sessions-toggle-btn:hover {
          background-color: rgba(44, 62, 80, 0.04);
        }

        .sessions-toggle-btn .pi {
          font-size: 0.75rem;
        }

        .exam-subtitle {
          color: #34495e;
          margin-top: 0.5rem;
          font-weight: 500;
        }

        .exam-program {
          color: #7f8c8d;
          margin-top: 0.25rem;
          font-size: 0.9rem;
        }

        .exam-info-group {
          display: flex;
          gap: 1.25rem;
          align-items: stretch;
        }

        .info-box {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: #ffffff;
          padding: 0.75rem 1.25rem;
          border-radius: 8px;
          min-width: 200px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }

        .info-box:hover {
          border-color: #cbd5e0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .info-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 6px;
          background: #f7fafc;
        }

        .scoring-box .info-icon {
          color: #4a5568;
        }

        .grade-box .info-icon {
          color: #2196F3;
        }

        .info-icon i {
          font-size: 1rem;
        }

        .info-content {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.25rem;
        }

        .info-label {
          color: #718096;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .info-value {
          display: flex;
          align-items: center;
        }

        .scoring-method {
          background: #f7fafc;
          color: #4a5568;
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
          white-space: nowrap;
        }

        .grade-display {
          background: #e3effd;
          color: #2196F3;
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 600;
          white-space: nowrap;
        }

        .exam-content {
          padding: 1.5rem;
        }

        .sessions-container {
          margin-top: 0;
        }

        .sessions-table {
          border: 1px solid #e9ecef;
          border-radius: 6px;
          overflow: hidden;
        }

        .grade-value {
          font-weight: bold;
          padding: 4px 8px;
          border-radius: 4px;
        }

        .grade-value.passing {
          color: #2196F3;
        }

        .grade-value.failing {
          color: #f44336;
        }

        .p-divider {
          margin: 0;
        }

        .p-tag {
          font-size: 0.875rem;
        }

        .sessions-info {
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
});
